// src/app/services/fare-settings/fare-settings.service.ts

import { Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { BehaviorSubject } from 'rxjs';
import { FareSettings } from '../../interfaces/fare-settings.interface';

@Injectable({ providedIn: 'root' })
export class FareSettingsService {
  private _settings = new BehaviorSubject<FareSettings>({} as FareSettings);
  get settings() {
    return this._settings.asObservable();
  }

  private cityCache = new Map<string, FareSettings>();
  private legacyCache: FareSettings | null = null;

  constructor(private api: ApiService) {}

  /** Legacy global settings (old apps behavior) */
  async getFareSettings(force = false): Promise<FareSettings> {
    const legacy = await this.getLegacyFareSettings(force);
    this._settings.next(legacy);
    return legacy;
  }

  /**
   * City-specific fare settings:
   * 1) fare_settings/{cityId}
   * 2) (temporary fallback) settings/{cityId} if it still exists
   * 3) legacy global: first doc in settings
   */
  async getFareSettingsForCity(cityId: string, force = false): Promise<FareSettings> {
    try {
      if (!cityId) {
        const legacy = await this.getLegacyFareSettings(force);
        this._settings.next(legacy);
        return legacy;
      }

      if (!force && this.cityCache.has(cityId)) {
        const cached = this.cityCache.get(cityId)!;
        this._settings.next(cached);
        return cached;
      }

      // ✅ 1) NEW location
      const citySnap: any = await this.api.getDocById(`fare_settings/${cityId}`);
      if (citySnap?.exists?.()) {
        const normalized = this.normalizeSettings(cityId, citySnap.data());
        this.cityCache.set(cityId, normalized);
        this._settings.next(normalized);
        return normalized;
      }

      // ✅ 2) TEMP fallback (in case some city docs still exist in /settings)
      const oldSnap: any = await this.api.getDocById(`settings/${cityId}`);
      if (oldSnap?.exists?.()) {
        const normalized = this.normalizeSettings(cityId, oldSnap.data());
        this.cityCache.set(cityId, normalized);
        this._settings.next(normalized);
        return normalized;
      }

      // ✅ 3) legacy global
      const legacy = await this.getLegacyFareSettings(force);
      this._settings.next(legacy);
      return legacy;
    } catch (e) {
      console.error('getFareSettingsForCity error:', e);
      const legacy = await this.getLegacyFareSettings(false);
      this._settings.next(legacy);
      return legacy;
    }
  }

  clearCache(): void {
    this.cityCache.clear();
    this.legacyCache = null;
    this._settings.next({} as FareSettings);
  }

  // -----------------------
  // Internal
  // -----------------------
  private async getLegacyFareSettings(force = false): Promise<FareSettings> {
    if (!force && this.legacyCache?.id) return this.legacyCache;

    const querySnapshot: any = await this.api.getDocs('settings');
    const list: FareSettings[] = querySnapshot.docs.map((doc: any) =>
      this.normalizeSettings(doc.id, doc.data())
    );

    const legacy = list[0] || ({} as FareSettings);
    this.legacyCache = legacy?.id ? legacy : null;

    return legacy;
  }

  private normalizeSettings(id: string, item: any): FareSettings {
    const ranges = (item?.ranges || []).map((r: any) => ({
      start: Number(r?.start ?? 0),
      end: r?.end != null ? Number(r.end) : undefined,
      price_per_km: Number(r?.price_per_km ?? 0),
      variation: r?.variation != null ? Number(r.variation) : 0,
    }));

    return {
      ...item,
      id,
      base_distance: Number(item?.base_distance ?? 0),
      base_amount: Number(item?.base_amount ?? 0),
      radius: Number(item?.radius ?? 0),
      amount_per_km: Number(item?.amount_per_km ?? 0),
      restaurant_share: Number(item?.restaurant_share ?? 0),
      rider_share: Number(item?.rider_share ?? 0),
      tax: Number(item?.tax ?? 0),
      ranges,
      fee_mode: item?.fee_mode || 'ranges',
    } as FareSettings;
  }
}














// // src\app\services\fare-settings\fare-settings.service.ts

// import { Injectable } from '@angular/core';
// import { ApiService } from '../api/api.service';
// import { BehaviorSubject } from 'rxjs';
// import { FareSettings } from '../../interfaces/fare-settings.interface';

// @Injectable({
//   providedIn: 'root'
// })
// export class FareSettingsService {

//   private _settings = new BehaviorSubject<FareSettings>({} as FareSettings);

//   get settings() {
//     return this._settings.asObservable();
//   }

//   constructor(private api: ApiService) {}

//   async getFareSettings() {
//     try {
//       const currentSettings = this._settings.value;
//       if (currentSettings?.id) {
//         return currentSettings; // Return cached settings if available
//       }

//       console.log('Fetching fare settings...');
//       const querySnapshot: any = await this.api.getDocs('settings');
//       const data: FareSettings[] = querySnapshot.docs.map((doc: any) => {
//         const item: any = doc.data();
//         return {
//           ...item,
//           id: doc.id,
//           ranges: item.ranges || [] // Ensure ranges is initialized as an array
//         };
//       });

//       const fetchedSettings = data[0] || ({} as FareSettings); // Use the first document or an empty object
//       this._settings.next(fetchedSettings); // Cache the fetched settings
//       return fetchedSettings;
//     } catch (e) {
//       throw e;
//     }
//   }
// }



